import { defineColumns } from './piColumns.js';
import { EMPTY, formatDate, formatDecimal, formatDuration, formatInteger, formatPercent, isNumber } from './piFormat.js';
import { listOf } from './piRequests.js';
import { deltaAllowed, normalizeMetric, showsValue } from './piState.js';
import { countText, metricCell, numberOf, shareOf } from './piTable.js';

export const RESULT_INFO = {
  success: { label: 'Sucesso', tone: 'ok', description: 'A operação concluiu o que se propõe.' },
  business: { label: 'Falha funcional', tone: 'warn', description: 'A regra de negócio recusou (sem permissão, período fechado, duplicado).' },
  technical: { label: 'Falha técnica', tone: 'bad', description: 'Exceção ou erro de infraestrutura.' },
  unknown: { label: 'Resultado não informado', tone: 'muted', description: 'A API não informou o tipo de resultado.' },
};

export const FAILURE_CATEGORY_LABELS = {
  no_match: 'Nenhum candidato',
  below_threshold: 'Abaixo do limiar da empresa',
  no_face: 'Nenhum rosto detectado',
  multiple_faces: 'Mais de um rosto',
  user_not_found: 'Colaborador não encontrado',
  mismatch_user: 'Rosto de outro colaborador',
  engine_error: 'Erro do motor',
  infra_error: 'Erro de infraestrutura',
  unknown_outcome: 'Desfecho desconhecido',
};

export const DROP_RULE = { thresholdPp: 2, window: 7, minAttempts: 100 };

const rateOf = (row) => (row && row.successRate ? row.successRate.metric : null);

const previousRate = (row) => {
  const metric = rateOf(row);
  return metric && isNumber(metric.previous) ? metric.previous : null;
};

const rateDelta = (row) => {
  const metric = rateOf(row);
  return metric && deltaAllowed(metric) ? metric.deltaAbs : null;
};

export const OPERATION_COLUMNS = defineColumns([
  { key: 'label', label: 'Operação', type: 'text', infoKey: 'operations.operation', sortable: true, startDescending: false },
  { key: 'operationKey', label: 'Chave da operação', type: 'text', infoKey: 'operations.operation', screen: false },
  { key: 'note', label: 'Observação', type: 'text', infoKey: 'operations.note', screen: false },
  { key: 'attempts', label: 'Tentativas', type: 'int', infoKey: 'operations.attempts', sortable: true, startDescending: true },
  { key: 'dupAttempts', label: 'Tentativas repetidas (mesma chave)', type: 'int', infoKey: 'operations.attempts', screen: false },
  { key: 'success', label: 'Sucessos', type: 'int', infoKey: 'operations.success', sortable: true, startDescending: true },
  { key: 'businessFail', label: 'Falha funcional', type: 'int', infoKey: 'operations.businessFail', sortable: true, startDescending: true },
  { key: 'businessShare', label: 'Falha funcional sobre as tentativas', type: 'percent', infoKey: 'operations.businessFail', screen: false },
  { key: 'technicalFail', label: 'Falha técnica', type: 'int', infoKey: 'operations.technicalFail', sortable: true, startDescending: true },
  { key: 'technicalShare', label: 'Falha técnica sobre as tentativas', type: 'percent', infoKey: 'operations.technicalFail', screen: false },
  { key: 'successValue', label: 'Taxa de sucesso', type: 'metric', unit: 'pct', infoKey: 'operations.successRate', sortable: true, startDescending: false, value: rateOf },
  { key: 'successPrevious', label: 'Taxa de sucesso no período anterior', type: 'percent', infoKey: 'operations.successCompare', screen: false, value: previousRate },
  { key: 'successDelta', label: 'Variação da taxa de sucesso (p.p.)', type: 'decimal', infoKey: 'operations.successCompare', screen: false, value: rateDelta },
  {
    key: 'p95', label: 'Duração p50 / p95 / p99', type: 'int', infoKey: 'operations.duration', sortable: true, startDescending: true, export: false,
    exportedAs: ['durationP50', 'durationP95', 'durationP99'],
  },
  { key: 'durationP50', label: 'Duração p50 (ms)', type: 'int', infoKey: 'operations.duration', screen: false, value: (row) => row.p50 },
  { key: 'durationP95', label: 'Duração p95 (ms)', type: 'int', infoKey: 'operations.duration', screen: false, value: (row) => row.p95 },
  { key: 'durationP99', label: 'Duração p99 (ms)', type: 'int', infoKey: 'operations.duration', screen: false, value: (row) => row.p99 },
  { key: 'trend', label: 'Tendência', type: 'text', infoKey: 'operations.trend', export: false },
]);

export const COMPARE_ONLY_KEYS = ['successPrevious', 'successDelta'];

export const operationColumns = (compare) => OPERATION_COLUMNS.filter((column) => compare === true || COMPARE_ONLY_KEYS.indexOf(column.key) === -1);

export const FAILURE_CATEGORY_COLUMNS = defineColumns([
  { key: 'category', label: 'Categoria', type: 'text', infoKey: 'operations.failureCategory', value: (row) => row.categoryText },
  { key: 'code', label: 'Código da categoria', type: 'text', infoKey: 'operations.failureCategory', screen: false, value: (row) => row.category },
  { key: 'type', label: 'Tipo', type: 'text', infoKey: 'operations.failureType', value: (row) => (row.hasType ? row.result.label : '') },
  { key: 'count', label: 'Ocorrências', type: 'int', infoKey: 'operations.failureCount' },
  { key: 'share', label: 'Participação entre as categorias', type: 'percent', infoKey: 'operations.failureCount' },
]);

export const failureColumns = (typeShown) => FAILURE_CATEGORY_COLUMNS.filter((column) => typeShown === true || column.key !== 'type');

export const DEFAULT_OPERATION_SORT = { sort: 'success', desc: true };

export const NO_FAILURE_SOURCE_NOTE = 'Falha não instrumentada nesta operação: só a conclusão é registrada, em Sucessos; sem tentativas nem taxa de sucesso.';

export const TREND_UNITS = ['pct', 'count'];

export const ATTEMPTS_LEAD = 'Tentativas e falhas só aparecem onde a falha é instrumentada; nas demais operações, Sucessos traz as conclusões registradas (na batida, as batidas gravadas) e a taxa fica n/d.';

export const TREND_LEAD = 'Tendência: taxa de sucesso por dia quando a falha é instrumentada; nas demais operações, volume de sucessos por dia.';

const NOT_INSTRUMENTED = /n[aã]o instrumentad/i;

const textOf = (value) => (value === null || value === undefined ? '' : String(value));

const capitalized = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

const reportedTrendUnit = (value) => {
  const unit = textOf(value).trim().toLowerCase();
  return TREND_UNITS.indexOf(unit) !== -1 ? unit : '';
};

export const trendInfo = (row, label, rateMeasured) => {
  const source = row || {};
  const unit = reportedTrendUnit(source.trendUnit) || (rateMeasured ? 'pct' : 'count');
  return { unit: unit, label: (unit === 'pct' ? 'Sucesso de ' : 'Volume de ') + label };
};

export const resultKind = (result) => {
  const key = textOf(result).trim().toLowerCase().replace(/[\s_-]/g, '');
  if (key === '1' || key === 'success' || key === 'sucesso') return 'success';
  if (key === '2' || key === 'businessfailure' || key === 'business' || key === 'funcional') return 'business';
  if (key === '3' || key === 'technicalfailure' || key === 'technical' || key === 'tecnica') return 'technical';
  return 'unknown';
};

export const resultInfo = (result) => {
  const kind = resultKind(result);
  return Object.assign({ kind: kind }, RESULT_INFO[kind]);
};

const durationText = (value) => (isNumber(value) ? formatDuration(value) : EMPTY);

export const failuresMeasured = (row) => {
  const source = row || {};
  if (source.failuresTracked === false || source.hasFailureSource === false) return false;
  if (!isNumber(numberOf(source.businessFail)) && !isNumber(numberOf(source.technicalFail))) return false;
  if (NOT_INSTRUMENTED.test(textOf(source.note))) return false;
  const rate = source.successRate && typeof source.successRate === 'object' ? source.successRate : {};
  if (rate.state === 'not_instrumented') return false;
  return !(rate.state === 'na' && NOT_INSTRUMENTED.test(textOf(rate.stateReason)));
};

export const operationRow = (row, compare) => {
  const source = row || {};
  const measured = failuresMeasured(source);
  const attempts = measured ? numberOf(source.attempts) : null;
  const reported = numberOf(source.success);
  const success = measured || isNumber(reported) ? reported : numberOf(source.attempts);
  const businessFail = measured ? numberOf(source.businessFail) : null;
  const technicalFail = measured ? numberOf(source.technicalFail) : null;
  const failures = (isNumber(businessFail) ? businessFail : 0) + (isNumber(technicalFail) ? technicalFail : 0);
  const successRate = metricCell(source.successRate, { compare: compare });
  const hasDuration = isNumber(source.p50) || isNumber(source.p95) || isNumber(source.p99);
  const businessShare = shareOf(businessFail, attempts);
  const technicalShare = shareOf(technicalFail, attempts);
  const label = textOf(source.label) || textOf(source.operationKey) || 'Operação';
  const noSuccessRate = successRate.metric.state === 'na' || successRate.metric.state === 'not_instrumented';
  const trend = trendInfo(source, label, measured && !noSuccessRate);
  return {
    key: textOf(source.operationKey) || textOf(source.label),
    operationKey: textOf(source.operationKey),
    label: label,
    attempts: attempts,
    success: success,
    successLabel: textOf(source.successLabel),
    businessFail: businessFail,
    technicalFail: technicalFail,
    dupAttempts: measured ? numberOf(source.dupAttempts) : null,
    attemptsText: countText(attempts),
    successText: countText(success),
    businessText: countText(businessFail),
    technicalText: countText(technicalFail),
    dupText: measured && isNumber(source.dupAttempts) && source.dupAttempts > 0 ? formatInteger(source.dupAttempts) + ' tentativas repetidas (mesma chave)' : '',
    businessShare: businessShare,
    technicalShare: technicalShare,
    businessShareText: businessShare === null ? '' : formatPercent(businessShare) + ' das tentativas',
    technicalShareText: technicalShare === null ? '' : formatPercent(technicalShare) + ' das tentativas',
    failRateText: measured && isNumber(attempts) && attempts > 0 ? formatPercent((failures / attempts) * 100) + ' de falha' : '',
    failuresMeasured: measured,
    failureTitle: measured ? '' : (textOf(source.note) || NO_FAILURE_SOURCE_NOTE),
    successRate: successRate,
    successValue: successRate.sortValue,
    noSuccessRate: noSuccessRate,
    p50: isNumber(source.p50) ? source.p50 : null,
    p95: isNumber(source.p95) ? source.p95 : null,
    p99: isNumber(source.p99) ? source.p99 : null,
    durationText: hasDuration ? durationText(source.p50) + ' / ' + durationText(source.p95) + ' / ' + durationText(source.p99) : '',
    hasDuration: hasDuration,
    trend: listOf(source.trend),
    trendUnit: trend.unit,
    trendLabel: trend.label,
    note: textOf(source.note),
  };
};

export const operationRows = (rows, compare) => listOf(rows).map((row, index) => {
  const view = operationRow(row, compare);
  if (!view.key) view.key = 'operation-' + index;
  return view;
});

const hasCategory = (row) => textOf(row.category).trim() !== '';

export const failureCategoryLabel = (category) => {
  const key = textOf(category).trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(FAILURE_CATEGORY_LABELS, key) ? FAILURE_CATEGORY_LABELS[key] : '';
};

export const failureRows = (rows) => {
  const list = listOf(rows).filter(hasCategory);
  const total = list.reduce((sum, row) => sum + (isNumber(row.count) ? row.count : 0), 0);
  return list
    .map((row, index) => {
      const info = resultInfo(row.result);
      const share = shareOf(row.count, total);
      const known = failureCategoryLabel(row.category);
      const categoryText = known || textOf(row.category);
      return {
        key: textOf(row.category) + ':' + info.kind + ':' + index,
        category: textOf(row.category),
        categoryText: categoryText,
        categoryKnown: known !== '',
        result: info,
        hasType: info.kind !== 'unknown',
        count: isNumber(row.count) ? row.count : null,
        countText: countText(row.count),
        share: share,
        shareText: share === null ? EMPTY : formatPercent(share),
        value: isNumber(row.count) ? row.count : null,
        valueText: countText(row.count) + (share === null ? '' : ' (' + formatPercent(share) + ')'),
        label: categoryText + (info.kind === 'unknown' ? '' : ' · ' + info.label),
        title: info.description,
      };
    })
    .sort((a, b) => (isNumber(b.count) ? b.count : -1) - (isNumber(a.count) ? a.count : -1));
};

export const failureTotals = (rows) => {
  const totals = { business: 0, technical: 0, unknown: 0 };
  listOf(rows).forEach((row) => {
    if (hasCategory(row)) return;
    const kind = resultKind(row.result);
    if (kind === 'success' || !isNumber(row.count)) return;
    totals[kind] += row.count;
  });
  return totals;
};

export const failureSummary = (rows) => {
  const totals = failureTotals(rows);
  const all = totals.business + totals.technical + totals.unknown;
  const shareText = (count) => {
    const share = shareOf(count, all);
    return share === null ? '' : formatPercent(share) + ' das falhas';
  };
  return {
    hasTotals: all > 0,
    businessText: formatInteger(totals.business),
    technicalText: formatInteger(totals.technical),
    businessShareText: shareText(totals.business),
    technicalShareText: shareText(totals.technical),
  };
};

const sortedPoints = (points) => listOf(points)
  .filter((point) => typeof point.date === 'string' && point.date)
  .slice()
  .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

const reportedRate = (value) => {
  if (isNumber(value)) return value;
  if (!value || typeof value !== 'object') return null;
  const metric = normalizeMetric(value);
  return showsValue(metric) ? metric.value : null;
};

export const rateOfPoint = (point) => {
  const source = point || {};
  if (source.successRate !== undefined) return reportedRate(source.successRate);
  return shareOf(numberOf(source.success), numberOf(source.attempts));
};

export const markDrops = (points, rule) => {
  const settings = Object.assign({}, DROP_RULE, rule || {});
  const sorted = sortedPoints(points);
  const drops = [];
  sorted.forEach((point, index) => {
    const rate = rateOfPoint(point);
    const attempts = numberOf(point.attempts);
    if (rate === null || !isNumber(attempts) || attempts < settings.minAttempts) return;
    const window = sorted.slice(Math.max(0, index - settings.window), index)
      .filter((previous) => {
        const volume = numberOf(previous.attempts);
        return isNumber(volume) && volume >= settings.minAttempts;
      })
      .map(rateOfPoint)
      .filter((value) => value !== null);
    if (!window.length) return;
    const baseline = window.reduce((sum, value) => sum + value, 0) / window.length;
    const delta = rate - baseline;
    if (delta <= -settings.thresholdPp) {
      drops.push({
        date: point.date,
        rate: rate,
        baseline: baseline,
        deltaPp: delta,
        text: formatDate(point.date) + ': ' + formatPercent(rate) + ' (média anterior ' + formatPercent(baseline) +
          ', ' + formatDecimal(delta, 1).replace('-', '−') + ' p.p.)',
      });
    }
  });
  return drops;
};

export const volumeTitle = (measured, successLabel) => (
  measured === false ? capitalized(textOf(successLabel) || 'sucessos') + ' por dia' : 'Tentativas, sucessos e falhas'
);

const successOfPoint = (point, measured) => {
  const success = numberOf(point.success);
  return measured === false && !isNumber(success) ? numberOf(point.attempts) : success;
};

export const operationSeries = (points, measured, successLabel) => {
  const sorted = sortedPoints(points);
  const dropDates = markDrops(sorted).map((drop) => drop.date);
  const success = {
    key: 'success',
    label: measured === false ? capitalized(textOf(successLabel) || 'sucessos') : 'Sucessos',
    points: sorted.map((point) => ({ date: point.date, value: successOfPoint(point, measured) })),
  };
  const volume = measured === false ? [success] : [
    { key: 'attempts', label: 'Tentativas', points: sorted.map((point) => ({ date: point.date, value: numberOf(point.attempts) })) },
    success,
  ];
  if (measured !== false) {
    volume.push(
      { key: 'businessFail', label: 'Falhas funcionais', higherIsBetter: false, points: sorted.map((point) => ({ date: point.date, value: numberOf(point.businessFail) })) },
      { key: 'technicalFail', label: 'Falhas técnicas', higherIsBetter: false, points: sorted.map((point) => ({ date: point.date, value: numberOf(point.technicalFail) })) }
    );
  }
  return {
    rate: [{
      key: 'successRate',
      label: 'Taxa de sucesso',
      area: true,
      points: sorted.map((point) => ({ date: point.date, value: rateOfPoint(point), partial: dropDates.indexOf(point.date) !== -1 })),
    }],
    volume: volume,
  };
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piOperationsModel.js